// CivicAPI (civicapi.org) — a free, no-auth, CORS-open public API of live
// election results. One call to /race/{id} returns statewide candidate totals
// AND per-county breakdowns AND a last_updated timestamp, so it drives the
// headline, the geography table, and provenance all at once.
//
// It is a THIRD-PARTY aggregator (not an official Secretary of State feed), so
// it flows through the app labeled "CivicAPI" with its own timestamp — reconciled
// against any other enabled source, never presented as official truth.
//
// Find a race id: GET https://civicapi.org/api/v2/race/search?province=MI&country=US
// (filter by election_name), then set `civicRaceId` in the vote provider config.
import type { VoteProvider } from '../../types/providers.ts';
import { ProviderError } from '../../types/providers.ts';
import type { CandidateResult, CountyResult, CallStatus } from '../../types/results.ts';
import type { RaceConfig } from '../../types/config.ts';
import { nowIso } from '../../lib/time.ts';

const BASE = 'https://civicapi.org/api/v2';

interface CivicCandidate {
  name: string;
  party?: string;
  votes?: number;
  percent?: number;
  winner?: boolean;
}
interface CivicRegion {
  name?: string;
  type?: string;
  fips?: string;
  percent_reporting?: number;
  candidates?: CivicCandidate[];
}
interface CivicRace {
  election_name?: string;
  percent_reporting?: number;
  last_updated?: string;
  is_disputed?: boolean;
  candidates?: CivicCandidate[];
  region_results?: Record<string, CivicRegion>;
}

function num(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

/** Map a CivicAPI candidate name to the config candidate id (so the UI renders
 *  the configured color/label/pattern). Unmatched names get a stable slug. */
function toId(race: RaceConfig, name: string): string {
  const n = name.toLowerCase();
  const c = race.candidates.find(
    (c) => n.includes(c.name.toLowerCase()) || c.name.toLowerCase().includes(n),
  );
  return c ? c.id : n.replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}

function mapCandidates(race: RaceConfig, list: CivicCandidate[] | undefined): CandidateResult[] {
  return (list ?? []).map((c) => ({
    id: toId(race, c.name),
    votes: num(c.votes),
    percent: num(c.percent),
  }));
}

export const civicapiProvider: VoteProvider = {
  id: 'civicapi',
  kind: 'votes',
  async getResults(race, cfg, signal) {
    const id = cfg.civicRaceId;
    if (id == null || id === '') {
      throw new ProviderError('not_configured', 'No CivicAPI race id configured');
    }

    let res: Response;
    try {
      res = await fetch(`${BASE}/race/${encodeURIComponent(String(id))}`, {
        signal,
        headers: { accept: 'application/json' },
      });
    } catch (e) {
      if (signal?.aborted) throw e;
      throw new ProviderError('unreachable', 'CivicAPI is unreachable');
    }
    if (res.status === 404) throw new ProviderError('not_configured', `CivicAPI race ${id} not found`);
    if (!res.ok) throw new ProviderError('unreachable', `CivicAPI HTTP ${res.status}`);

    let d: CivicRace;
    try {
      d = (await res.json()) as CivicRace;
    } catch {
      throw new ProviderError('invalid', 'CivicAPI returned invalid JSON');
    }

    const winner = (d.candidates ?? []).find((c) => c.winner);
    const callStatus: CallStatus = winner ? 'projected' : 'not_called';

    const counties: CountyResult[] = Object.values(d.region_results ?? {}).map((r) => ({
      name: r.name ?? '',
      fips: r.fips,
      reportingPercent: num(r.percent_reporting),
      candidates: mapCandidates(race, r.candidates),
    }));

    return {
      source: 'CivicAPI',
      sourceId: 'civicapi',
      fetchedAt: d.last_updated ?? nowIso(),
      reportingPercent: num(d.percent_reporting),
      estimatedVotesRemaining: null,
      candidates: mapCandidates(race, d.candidates),
      counties,
      callStatus,
      callFor: winner ? toId(race, winner.name) : null,
      entry: 'api',
    };
  },
};
